/**
 * Finding, in the rendered preview, the ROWS of a repeater and the place to hang its "add" button.
 *
 * ⚠️ THIS IS `fieldTargets.ts` ONE LEVEL DOWN, AND IT IS HERE FOR THE SAME REASON. The renderer is not
 * asked and is not changed: a `data-list-index` emitted by `@ksa/render` would ship to every published
 * page, or else the preview would stop being byte-for-byte what publishes (CLAUDE.md §2.2, §3.3). So a
 * row is located the way a field is — by the copy inside it, which the props and the language already
 * tell us.
 *
 * ⚠️ MATCHING ON COPY RATHER THAN ON POSITION IS NOT FASTIDIOUSNESS, IT IS THE ONLY CORRECT ANSWER
 * HERE. Half the blocks with a repeater FILTER it before rendering: the footer drops links with no
 * label, the FAQ drops questions with no question, `contact` drops an hours row that is blank on both
 * sides. So the third `<li>` on the screen is routinely not the third row in the document — and an
 * editor that opened row 3 because the customer tapped the third thing would silently edit somebody
 * else's Friday hours. Rows that cannot be located are simply not tappable, which is the same
 * fail-safe the field targets have.
 *
 * ⚠️ AND THE PLACEMENT OF THE ADD BUTTON IS DECLARED PER BLOCK, WITH A DEFAULT THAT IS RIGHT MORE
 * OFTEN THAN NOT. See `RULES`.
 */

import { resolve, type Block, type BlockDef, type FieldSpec, type Lang } from '@ksa/model'
import { findText } from './fieldTargets.ts'

export type ListTarget = {
  key: string
  /**
   * One entry per row **in document order**, `null` where the row could not be located — a row the
   * renderer filtered out, or one with nothing in it to match on. The index into this array is the
   * index into the document's array, which is the whole point.
   */
  rows: (HTMLElement | null)[]
  /** Where the add button hangs. `null` when nothing is rendered to hang it on. */
  anchor: HTMLElement | null
  /** Whether `anchor` is a heading, which decides whether the button is measured against the text or
   *  against the box. See `SelectionLayer`. */
  anchorIsHeading: boolean
}

/** The sub-field kinds that put something matchable on the screen. */
const TEXTUAL = new Set(['text', 'longText'])

type Rule = {
  /**
   * How to find the rows when their copy cannot: a repeater of pictures has no text at all. Only
   * safe for a block that renders EVERY row — a by-order rule on a filtered list is the bug this
   * file exists to avoid, so each one below states why its block is safe.
   */
  rows?: (root: HTMLElement) => HTMLElement[]
  /** Where the add button goes, when the default is wrong. */
  anchor?: (root: HTMLElement) => HTMLElement | null
}

/**
 * Per-block placement.
 *
 * **The default**, which most blocks want and none of them declare: the rows' own common ancestor is
 * the list's container, and if the element immediately before that container is a heading, the button
 * hangs off the heading. That is what puts the `+` beside «Opening hours» in `contact` and beside the
 * section heading in `features` — the two places a customer would point at to say "another one of
 * these" — without either block knowing the editor exists.
 *
 * ⚠️ A RULE HERE IS EDITOR KNOWLEDGE ABOUT RENDERED MARKUP, WHICH IS WHY IT IS IN `apps/web` AND NOT
 * IN THE REGISTRY. `@ksa/model` is imported by the API and by the Worker and describes what a block
 * MEANS; `figure` and `.s-grid` are what one block's component happens to emit. Putting a CSS
 * selector in the registry would make the document model depend on a stylesheet.
 */
const RULES: Record<string, Record<string, Rule>> = {
  gallery: {
    // ⚠️ A GALLERY ROW IS A PICTURE AND AN OPTIONAL CAPTION, so most rows have no copy to match. By
    // order is safe here and only here-ish: `GalleryBlock` maps the array straight through with no
    // filter, so the nth `<figure>` IS the nth row. If it ever grows a filter, this becomes wrong in
    // exactly the way the header of this file describes, and the fix is to delete this rule.
    items: { rows: (root) => [...root.querySelectorAll<HTMLElement>('figure')] },
  },
  team: {
    // Same argument: `TeamBlock` renders every member, photo first, so the nth `<li>` is the nth
    // member — and a member with no name yet would otherwise be unreachable, which is precisely the
    // one you have just added and want to fill in.
    members: { rows: (root) => [...root.querySelectorAll<HTMLElement>('ul > li')] },
  },
  navbar: {
    // The links have no heading above them; the row of links is the thing to add to.
    items: { anchor: (root) => root.querySelector<HTMLElement>('.s-nav__links') },
  },
}

/**
 * The rows and the anchor for every `list` field on this block.
 *
 * `root` is the block's own rendered element.
 */
export function listTargets(root: HTMLElement, block: Block, def: BlockDef, lang: Lang): ListTarget[] {
  const out: ListTarget[] = []

  for (const spec of def.fields) {
    if (spec.type.kind !== 'list') continue
    const rule = RULES[block.type]?.[spec.key]
    const value = block.props[spec.key]
    const rows = Array.isArray(value) ? (value as Record<string, unknown>[]) : []

    // A by-order rule answers for every row or for none; fewer elements than the document holds
    // means the renderer drew fewer, and the tail is simply not tappable.
    const byOrder = rule?.rows?.(root)
    const item = spec.type.item
    const located = byOrder ? rows.map((_, index) => byOrder[index] ?? null) : locateRows(root, rows, item, lang)

    const drawn = located.filter((element): element is HTMLElement => element !== null)
    const container = commonAncestor(drawn)
    const explicit = rule?.anchor?.(root) ?? null
    const heading = container ? headingBefore(container) : null

    /**
     * ⚠️ THE LAST ROW BEATS THE CONTAINER, AND THAT IS A FIX RATHER THAN A PREFERENCE. A container is
     * as wide as the column it is in, so "just after it" is the far edge of the page: on the nav bar
     * — a flex row of three links in a box stretched to the full width — the `+` landed at x=397 on a
     * 412 px phone, half of it past the edge of the frame and the whole of it invisible. Hanging it
     * off the last ROW puts it after «Visit», which is where a person would point to say "and one
     * more".
     *
     * ⚠️ THE CONTAINER IS STILL THE FALLBACK, FOR THE LIST THAT HAS NO ROWS YET. Nothing is rendered
     * to hang the button on then, which is why `navbar` keeps its explicit rule: an empty nav bar
     * still has a `.s-nav__links` to point at.
     */
    const last = drawn[drawn.length - 1] ?? null
    const anchor = heading ?? last ?? explicit ?? container

    out.push({ key: spec.key, rows: located, anchor, anchorIsHeading: anchor !== null && anchor === heading })
  }

  return out
}

/**
 * Every row of one list, found by what is written in each.
 *
 * ⚠️ A ROW IS THE COMMON ANCESTOR OF ITS OWN STRINGS, NOT THE ELEMENT OF THE FIRST ONE. A row is
 * `<dt>Saturday to Thursday</dt><dd>18:00 – 00:00</dd>`: claiming the `<dt>` would make half of it
 * tappable and leave the other half opening the block. The ancestor of both is the row.
 *
 * ⚠️ ONE `claimed` SET ACROSS ALL THE ROWS, NOT ONE PER ROW. Two rows reading «Friday» would otherwise
 * both resolve to the first `<dt>` on the screen, and tapping the second would edit the first — the
 * identical defect `findText` already guards against between two FIELDS, and repeaters are where
 * duplicate strings actually happen.
 *
 * ⚠️ AND A ROW WHOSE ELEMENT SWALLOWS ANOTHER ROW IS REFUSED, NOT GUESSED. If the markup gives the
 * rows no element of their own, every one of them resolves to the wrapper around all of them, and
 * every tap would open the same row. Refusing means the customer opens the whole block and edits the
 * row in the list — the behaviour that existed before any of this, which is the right thing to fall
 * back to.
 */
function locateRows(
  root: HTMLElement,
  rows: Record<string, unknown>[],
  subFields: FieldSpec[],
  lang: Lang,
): (HTMLElement | null)[] {
  const claimed = new Set<Element>()

  const located = rows.map((row) => {
    const found: HTMLElement[] = []
    for (const sub of subFields) {
      if (!TEXTUAL.has(sub.type.kind)) continue
      const value = row[sub.key]
      const text = value && typeof value === 'object' ? resolve(value as never, lang) : ''
      if (!text) continue
      const element = findText(root, text, claimed)
      if (!element) continue
      claimed.add(element)
      found.push(element)
    }
    return commonAncestor(found)
  })

  return located.map((element, index) =>
    element && located.every((other, i) => i === index || !other || !element.contains(other)) ? element : null,
  )
}

/** The innermost element containing all of them. `null` for none; the element itself for one. */
function commonAncestor(elements: HTMLElement[]): HTMLElement | null {
  if (elements.length === 0) return null
  let node: HTMLElement | null = elements[0]!
  while (node && !elements.every((element) => node!.contains(element))) node = node.parentElement
  return node
}

/**
 * ⚠️ THE HEADING IMMEDIATELY BEFORE THE LIST, AND ONLY IF IT IS ONE. `previousElementSibling` rather
 * than a search: the thing a customer reads as the list's title is the thing touching it. A search
 * upwards would find the section's own `<h2>` for a list nested three levels down and put the add
 * button somewhere that means nothing.
 */
function headingBefore(container: HTMLElement): HTMLElement | null {
  const previous = container.previousElementSibling
  if (previous instanceof HTMLElement && /^H[1-6]$/.test(previous.tagName)) return previous
  return null
}
